/**
 * Real-Time Speech Recorder
 *
 * Tap once to start, text appears live, tap again to stop.
 * Uses ElevenLabs real-time Speech-to-Text API via WebSocket.
 */

export type RecorderState =
  | "idle"        // Ready to record
  | "connecting"  // WebSocket connecting
  | "ready"       // Connected, speak now
  | "recording"   // Actively transcribing
  | "processing"  // Finalizing
  | "error";      // Error occurred

export interface RecorderEvents {
  onStateChange: (state: RecorderState) => void;
  onPartialTranscript: (text: string) => void;  // Live text updates
  onFinalTranscript: (text: string) => void;    // Complete text when done
  onError: (error: Error) => void;
  onVolumeLevel: (level: number) => void;       // 0-1 for visualizing
  onDebugLog?: (type: "request" | "response" | "error" | "info", message: string, data?: any) => void;
}

export class RealtimeSpeechRecorder {
  private proxyUrl: string;
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private processor: ScriptProcessorNode | null = null;
  private analyser: AnalyserNode | null = null;
  private state: RecorderState = "idle";
  private events: Partial<RecorderEvents> = {};
  private partial = "";
  private committed = "";
  private volumeFrame: number | null = null;
  private recordingTimer: number | null = null;
  private readonly MAX_RECORDING_TIME = 30000; // 30 seconds in milliseconds

  constructor(proxyUrl: string) {
    this.proxyUrl = proxyUrl;
  }

  setEventHandlers(events: Partial<RecorderEvents>): void {
    this.events = events;
  }

  getState(): RecorderState {
    return this.state;
  }

  private setState(state: RecorderState): void {
    this.state = state;
    this.events.onStateChange?.(state);
  }

  // ════════════════════════════════════════════════════════════
  // TAP TO START
  // ════════════════════════════════════════════════════════════
  async start(): Promise<void> {
    if (this.state !== "idle") return;

    this.setState("connecting");
    this.partial = "";
    this.committed = "";

    try {
      // Get single-use token from proxy
      this.events.onDebugLog?.("request", "STT: Fetching single-use token", {
        url: `${this.proxyUrl}/stt/token`,
        method: "POST",
      });

      const res = await fetch(`${this.proxyUrl}/stt/token`, {
        method: "POST",
      });

      this.events.onDebugLog?.("response", `STT: Token response (${res.status})`, {
        status: res.status,
        statusText: res.statusText,
        ok: res.ok,
      });

      if (!res.ok) {
        const errorText = await res.text();
        this.events.onDebugLog?.("error", `STT: Token fetch failed (${res.status})`, {
          status: res.status,
          statusText: res.statusText,
          error: errorText,
        });
        throw new Error(`Token fetch failed: ${res.status} - ${errorText}`);
      }

      const { token, wsUrl } = await res.json();

      this.events.onDebugLog?.("response", "STT: Token received", {
        hasToken: !!token,
        wsUrl,
      });

      // Build WebSocket URL with token in query parameter (client-side authentication)
      const url = new URL(wsUrl);
      url.searchParams.set("model_id", "scribe_v2_realtime");
      url.searchParams.set("language_code", "en");
      url.searchParams.set("token", token);

      this.events.onDebugLog?.("request", "STT: Connecting to ElevenLabs WebSocket", {
        url: url.toString().replace(token, "***"),  // Redact token from logs
        model: "scribe_v2_realtime",
        language: "en",
        authMethod: "single-use token in query parameter",
      });

      // Connect directly to ElevenLabs with token
      this.ws = new WebSocket(url.toString());

      this.ws.onopen = async () => {
        this.events.onDebugLog?.("response", "STT: WebSocket connected (authenticated via token)");

        await this.startMic();
        this.setState("ready");

        // Brief moment then recording
        setTimeout(() => {
          if (this.state === "ready") {
            this.setState("recording");
            // Start 30-second timer
            this.recordingTimer = window.setTimeout(() => {
              this.events.onDebugLog?.("info", "STT: 30-second recording limit reached, stopping");
              this.stop();
            }, this.MAX_RECORDING_TIME);
          }
        }, 300);
      };

      this.ws.onmessage = (e) => this.handleMessage(e);

      this.ws.onerror = (event) => {
        this.events.onDebugLog?.("error", "STT: WebSocket error", {
          event: event.type,
          readyState: this.ws?.readyState,
        });
        this.events.onError?.(new Error("WebSocket error"));
        this.cleanup();
        this.setState("error");
      };

      this.ws.onclose = (event) => {
        this.events.onDebugLog?.("response", "STT: WebSocket closed", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        });
        if (this.state === "recording" || this.state === "ready") {
          this.finalize();
        }
      };
    } catch (err) {
      this.events.onError?.(err as Error);
      this.setState("error");
    }
  }

  // ════════════════════════════════════════════════════════════
  // TAP TO STOP
  // ════════════════════════════════════════════════════════════
  async stop(): Promise<void> {
    if (this.state !== "recording" && this.state !== "ready") return;

    this.setState("processing");

    // Send commit to finalize
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ message_type: "commit" }));
      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    this.finalize();
  }

  private finalize(): void {
    const text = (this.committed + " " + this.partial).trim();
    if (text) this.events.onFinalTranscript?.(text);
    this.cleanup();
    this.setState("idle");
  }

  // ════════════════════════════════════════════════════════════
  // MICROPHONE
  // ════════════════════════════════════════════════════════════
  private async startMic(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
      },
    });

    this.audioCtx = new AudioContext({ sampleRate: 16000 });
    const source = this.audioCtx.createMediaStreamSource(this.stream);

    // Volume analyser
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 256;
    source.connect(this.analyser);
    this.monitorVolume();

    // Audio processor - sends PCM to WebSocket
    this.processor = this.audioCtx.createScriptProcessor(4096, 1, 1);
    source.connect(this.processor);
    this.processor.connect(this.audioCtx.destination);

    this.processor.onaudioprocess = (e) => {
      if (
        this.ws?.readyState !== WebSocket.OPEN ||
        this.state !== "recording"
      )
        return;

      const input = e.inputBuffer.getChannelData(0);
      const pcm = new Int16Array(input.length);
      for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }

      // Convert to base64
      const bytes = new Uint8Array(pcm.buffer);
      let binary = "";
      for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);

      this.ws!.send(
        JSON.stringify({
          message_type: "input_audio_chunk",
          audio_base_64: base64,
          sample_rate: 16000,
        })
      );
    };
  }

  private handleMessage(e: MessageEvent): void {
    try {
      const data = JSON.parse(e.data);

      // Log ALL incoming WebSocket messages
      this.events.onDebugLog?.("response", `STT: WebSocket message received`, {
        message_type: data.message_type,
        data: data,
      });

      switch (data.message_type) {
        case "authentication_success":
          this.events.onDebugLog?.("response", "STT: Authentication successful");
          break;

        case "authentication_failed":
        case "auth_error":
          this.events.onDebugLog?.("error", "STT: Authentication failed", data);
          this.events.onError?.(new Error(data.error || "Authentication failed"));
          this.cleanup();
          this.setState("error");
          break;

        case "partial_transcript":
          this.partial = data.text || "";
          this.events.onPartialTranscript?.(
            (this.committed + " " + this.partial).trim()
          );
          break;

        case "committed_transcript":
        case "committed_transcript_with_timestamps": {
          const text =
            data.words?.map((w: any) => w.text).join("") ||
            data.text ||
            "";
          this.committed += (this.committed ? " " : "") + text;
          this.partial = "";
          this.events.onPartialTranscript?.(this.committed);
          break;
        }

        case "error":
          this.events.onDebugLog?.("error", "STT: Error from server", data);
          this.events.onError?.(new Error(data.error || "STT error"));
          break;

        default:
          this.events.onDebugLog?.("response", `STT: Unhandled message type: ${data.message_type}`, data);
      }
    } catch (err) {
      this.events.onDebugLog?.("error", "STT: Failed to parse WebSocket message", {
        error: err instanceof Error ? err.message : String(err),
        rawData: e.data,
      });
    }
  }

  private monitorVolume(): void {
    if (!this.analyser) return;
    const data = new Uint8Array(this.analyser.frequencyBinCount);

    const update = () => {
      if (!this.analyser || this.state === "idle") return;
      this.analyser.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b) / data.length;
      this.events.onVolumeLevel?.(Math.min(1, avg / 128));
      this.volumeFrame = requestAnimationFrame(update);
    };
    update();
  }

  private cleanup(): void {
    if (this.volumeFrame) cancelAnimationFrame(this.volumeFrame);
    if (this.recordingTimer) {
      clearTimeout(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.processor?.disconnect();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.audioCtx?.close();
    this.ws?.close();
    this.processor = null;
    this.stream = null;
    this.audioCtx = null;
    this.ws = null;
    this.analyser = null;
  }

  cancel(): void {
    this.cleanup();
    this.setState("idle");
  }
}
